import React, { useState, useEffect } from 'react';
import {
  Card,
  Typography,
  Tag,
  Button,
  Row,
  Col,
  Spin,
  message,
  Select,
  Space,
  Empty,
  Pagination,
  Image
} from 'antd';
import {
  CalendarOutlined,
  CarOutlined,
  LogoutOutlined,
  EnvironmentOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { customerAPI } from '../services/api';

const { Title, Text } = Typography;
const { Option } = Select;

const Bookings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [bookings, setBookings] = useState([]);
  const [pagination, setPagination] = useState({});
  const [filters, setFilters] = useState({
    page: 1,
    limit: 10,
    bookingStatus: null,
  });

  useEffect(() => {
    fetchBookings();
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchBookings = async () => {
    try {
      setLoading(true);
      const response = await customerAPI.getBookings(filters);

      if (response.success) {
        setBookings(response.data.bookings);
        setPagination(response.data.pagination);
      } else {
        message.error('Failed to fetch bookings');
      }
    } catch (error) {
      console.error('Error fetching bookings:', error);
      message.error('Failed to fetch bookings');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
    message.success('Logged out successfully');
  };

  const getStatusColor = (status) => {
    const colors = {
      confirmed: 'green',
      in_progress: 'orange',
      completed: 'blue',
      cancelled: 'red',
      settled: 'purple',
    };
    return colors[status] || 'default';
  };

  const getStatusText = (status) => {
    const texts = {
      confirmed: 'Confirmed',
      in_progress: 'In Progress',
      completed: 'Completed',
      cancelled: 'Cancelled',
      settled: 'Settled',
    };
    return texts[status] || status;
  };

  const handleCardClick = (bookingId) => {
    navigate(`/bookings/${bookingId}`);
  };

  const handleStatusFilter = (value) => {
    setFilters(prev => ({
      ...prev,
      bookingStatus: value,
      page: 1,
    }));
  };

  const handlePageChange = (page) => {
    setFilters(prev => ({
      ...prev,
      page,
    }));
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      {/* Header */}
      <div style={{
        backgroundColor: 'white',
        padding: '16px 24px',
        borderBottom: '1px solid #f0f0f0',
        marginBottom: 24
      }}>
        <div style={{
          maxWidth: 1200,
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <img
            src="/logo.png"
            alt="Xoom Drives"
            style={{ height: 40 }}
          />
          <Button
            icon={<LogoutOutlined />}
            onClick={handleLogout}
            style={{ borderColor: '#2e5ec3', color: '#2e5ec3' }}
          >
            Logout
          </Button>
        </div>
      </div>

      <div className="container">
        {/* Page Header */}
        <div style={{ marginBottom: 24 }}>
          <Title level={2} style={{ marginBottom: 8 }}>My Bookings</Title>
          <Space>
            <Text type="secondary">Filter by status:</Text>
            <Select
              style={{ width: 150 }}
              placeholder="All Status"
              allowClear
              onChange={handleStatusFilter}
              value={filters.bookingStatus}
            >
              <Option value="confirmed">Confirmed</Option>
              <Option value="in_progress">In Progress</Option>
              <Option value="completed">Completed</Option>
              <Option value="cancelled">Cancelled</Option>
              <Option value="settled">Settled</Option>
            </Select>
          </Space>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '50px 0' }}>
            <Spin size="large" />
          </div>
        ) : bookings.length === 0 ? (
          <Empty
            description="No bookings found"
            style={{ padding: '50px 0' }}
          />
        ) : (
          <>
            {/* Bookings Grid */}
            <Row gutter={[24, 24]}>
              {bookings.map((booking) => (
                <Col xs={24} md={12} lg={8} key={booking.id}>
                  <Card
                    className="modern-booking-card"
                    hoverable
                    onClick={() => handleCardClick(booking.id)}
                    bodyStyle={{ padding: 0 }}
                  >
                    {/* Vehicle Image Section */}
                    <div className="booking-image-container">
                      {booking.vehicle?.vehicleImageUrls?.length > 0 ? (
                        <Image
                          src={booking.vehicle.vehicleImageUrls[0]}
                          alt={`${booking.vehicle.make} ${booking.vehicle.model}`}
                          style={{
                            width: '100%',
                            height: '200px',
                            objectFit: 'cover',
                            borderRadius: '12px 12px 0 0'
                          }}
                          preview={false}
                          fallback="/api/placeholder/400/200"
                        />
                      ) : (
                        <div className="booking-image-placeholder">
                          <CarOutlined style={{ fontSize: '48px', color: '#bfbfbf' }} />
                          <Text type="secondary">No Image Available</Text>
                        </div>
                      )}
                      <div className="booking-status-overlay">
                        <Tag
                          color={getStatusColor(booking.bookingStatus)}
                          style={{
                            margin: 0,
                            borderRadius: '16px',
                            padding: '4px 12px',
                            fontWeight: 500,
                            fontSize: '12px'
                          }}
                        >
                          {getStatusText(booking.bookingStatus)}
                        </Tag>
                      </div>
                    </div>

                    {/* Card Content */}
                    <div className="modern-booking-content">
                      {/* Vehicle Info */}
                      <div className="booking-vehicle-info">
                        <Title level={5} style={{ margin: 0, marginBottom: 4 }}>
                          {booking.vehicle.make} {booking.vehicle.model}
                        </Title>
                        <Text type="secondary" style={{ fontSize: '13px' }}>
                          {booking.bookingNumber}
                        </Text>
                      </div>

                      {/* Dates Section */}
                      <div className="modern-booking-dates">
                        <Row gutter={16}>
                          <Col span={12}>
                            <div className="date-info">
                              <div className="date-label">
                                <CalendarOutlined style={{ marginRight: 6, color: '#52c41a' }} />
                                <Text style={{ fontSize: '12px', fontWeight: 500 }}>Pickup</Text>
                              </div>
                              <Text style={{ fontSize: '14px', fontWeight: 600 }}>
                                {new Date(booking.pickupDate).toLocaleDateString('en-GB', {
                                  day: '2-digit',
                                  month: 'short'
                                })}
                              </Text>
                            </div>
                          </Col>
                          <Col span={12}>
                            <div className="date-info">
                              <div className="date-label">
                                <CalendarOutlined style={{ marginRight: 6, color: '#ff4d4f' }} />
                                <Text style={{ fontSize: '12px', fontWeight: 500 }}>Return</Text>
                              </div>
                              <Text style={{ fontSize: '14px', fontWeight: 600 }}>
                                {new Date(booking.returnDate).toLocaleDateString('en-GB', {
                                  day: '2-digit',
                                  month: 'short'
                                })}
                              </Text>
                            </div>
                          </Col>
                        </Row>
                      </div>

                      {/* Price and Vendor */}
                      <div className="booking-footer">
                        <div className="booking-price">
                          <Text type="secondary" style={{ fontSize: '12px' }}>Total Amount</Text>
                          <Title level={5} style={{ margin: 0, color: '#2e5ec3' }}>
                            AED {parseFloat(booking.totalAmount).toFixed(0)}
                          </Title>
                        </div>
                        {booking.vendor && (
                          <div className="booking-vendor">
                            <EnvironmentOutlined style={{ marginRight: 4, color: '#8c8c8c' }} />
                            <Text type="secondary" style={{ fontSize: '11px' }}>
                              {booking.vendor.companyName}
                            </Text>
                          </div>
                        )}
                      </div>
                    </div>
                  </Card>
                </Col>
              ))}
            </Row>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div style={{ textAlign: 'center', marginTop: 40 }}>
                <Pagination
                  current={pagination.currentPage}
                  total={pagination.totalItems}
                  pageSize={pagination.itemsPerPage}
                  onChange={handlePageChange}
                  showSizeChanger={false}
                  showTotal={(total, range) =>
                    `${range[0]}-${range[1]} of ${total} bookings`
                  }
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Bookings;